import {
  ReaImportAuditRecord,
  ReaImportWarning,
  ReaRecoveryAction,
  ReaTechnicalDetailItem,
} from 'src/app/models/fincas/rea.model';

import { ReaImportDataPresenter } from './rea-import-data.presenter';

/**
 * Maps raw audit records into user-facing labels and support details.
 */
export class ReaAuditPresenter {
  public static statusLabel(record: ReaImportAuditRecord): string {
    if (record.statusCode >= 200 && record.statusCode < 300) {
      return record.statusCode === 207 ? 'Con avisos' : 'Correcta';
    }

    return 'Fallida';
  }

  public static title(record: ReaImportAuditRecord): string {
    const userErrorTitle = this.getStringValue(record.summary, 'userErrorTitle');

    return userErrorTitle !== '' ? userErrorTitle : this.statusLabel(record);
  }

  public static description(record: ReaImportAuditRecord): string {
    const userErrorDescription = this.getStringValue(record.summary, 'userErrorDescription');

    return userErrorDescription !== '' ? userErrorDescription : record.statusMessage;
  }

  public static nextStep(record: ReaImportAuditRecord): string {
    return this.getStringValue(record.summary, 'userNextStep');
  }

  public static recoveryAction(record: ReaImportAuditRecord): ReaRecoveryAction | null {
    const recoveryAction = this.getRecordValue(record.summary, 'recoveryAction');

    return this.isReaRecoveryAction(recoveryAction) ? recoveryAction : null;
  }

  public static technicalDetails(record: ReaImportAuditRecord): ReaTechnicalDetailItem[] {
    const items: ReaTechnicalDetailItem[] = [];
    const technicalDetails = this.getRecordValue(record.summary, 'technicalDetails');

    this.appendTechnicalDetail(items, 'Import ID', record.importId);
    this.appendTechnicalDetail(items, 'Estado', record.statusCode);
    this.appendTechnicalDetail(items, 'Código interno', this.getStringValue(record.summary, 'errorCode'));
    this.appendTechnicalDetail(items, 'NIF/CIF', record.nif);
    this.appendTechnicalDetail(items, 'Código REA', record.codigoRea);
    this.appendTechnicalDetail(items, 'Código SIEX', record.codigoSiex);
    this.appendTechnicalDetail(items, 'Mensaje', record.statusMessage);

    if (this.isRecord(technicalDetails)) {
      this.appendTechnicalDetail(items, 'Trace ID', this.readStringFromRecord(technicalDetails, 'traceId'));
      this.appendTechnicalDetail(items, 'Bridge Trace ID', this.readStringFromRecord(technicalDetails, 'bridgeTraceId'));
      this.appendTechnicalDetail(items, 'Estado técnico', this.readStringFromRecord(technicalDetails, 'statusCode'));
    }

    this.appendWarnings(items, 'Aviso técnico', record.technicalWarnings ?? []);

    return items;
  }

  public static isError(record: ReaImportAuditRecord): boolean {
    return record.statusCode >= 400;
  }

  public static severity(statusCode: number): 'success' | 'warn' | 'danger' | 'info' {
    if (statusCode >= 200 && statusCode < 300) {
      return statusCode === 207 ? 'warn' : 'success';
    }

    if (statusCode >= 400) {
      return 'danger';
    }

    return 'info';
  }

  private static appendWarnings(items: ReaTechnicalDetailItem[], labelPrefix: string, warnings: ReaImportWarning[]): void {
    warnings.forEach((warning, index) => {
      const value = ReaImportDataPresenter.warningToSupportText(warning);

      this.appendTechnicalDetail(items, `${labelPrefix} ${index + 1}`, value);
    });
  }

  private static appendTechnicalDetail(items: ReaTechnicalDetailItem[], label: string, value: number | string | null | undefined): void {
    if (value == null || String(value).trim() === '') {
      return;
    }

    items.push({ label, value: String(value) });
  }

  private static getStringValue(source: unknown, key: string): string {
    const value = this.getRecordValue(source, key);

    return typeof value === 'string' ? value.trim() : '';
  }

  private static getRecordValue(source: unknown, key: string): unknown {
    if (!this.isRecord(source)) {
      return null;
    }

    return source[key];
  }

  private static readStringFromRecord(source: Record<string, unknown>, key: string): string {
    const value = source[key];

    if (typeof value === 'number') {
      return String(value);
    }

    return typeof value === 'string' ? value.trim() : '';
  }

  private static isReaRecoveryAction(value: unknown): value is ReaRecoveryAction {
    return this.isRecord(value)
      && value['type'] === 'REA_AUTHORIZATION_GUIDE'
      && typeof value['label'] === 'string'
      && typeof value['url'] === 'string';
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
  }
}
